import { FC } from 'react';
import { State } from 'redux/initialState';
import { format } from 'date-fns';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';

import { Board, Council, CouncilParticipant } from 'types';
import { DATE_FORMAT, TIME_FORMAT } from 'constants/defaultConstants';
import councilStatus from 'modules/administratives/constants/councilStatus';
import CouncilBadges from 'modules/administratives/components/CouncilBadges';

import { getCouncilDetailsUrlByRole } from 'modules/administratives/utils/getCouncilDetailsUrl';
import { getMemberIdFromCouncilParticipants } from 'modules/administratives/pages/CouncilsList/utils/getMemberIdFromCouncilParticipants';

import { getBoardStatusData } from 'utils/getBoardStatusData';
import { BoardEntityTypes } from 'types/Board';

import { getCouncilStatusData } from 'modules/administratives/pages/CouncilDetails/utils/getCouncilStatusData';
import { boardEntityTypes } from '../../BoardsListHeader/constants/boardEntityTypes';
import BoardBadge from '../../BoardBadge';
import ToggleMenu from '../../ToggleMenu';
import { useGetVisibility } from '../hooks/useGetVisibility';

type RecentBoardsSectionRowRowProps = {
  board: Council | Board;
  boardType: BoardEntityTypes;
  index: number;
  isAdmin: boolean;
  onRemoveBoard: (index: number) => void;
};

const RecentBoardsSectionRow: FC<RecentBoardsSectionRowRowProps> = ({
  board,
  boardType,
  index,
  isAdmin,
  onRemoveBoard,
}) => {
  const { t } = useTranslate();
  const history = useHistory();
  const { societyId, memberId } = useParams();

  const { isDirector } = useSelector(
    (state: State) => state.society?.role || {}
  );
  const user = useSelector((state: State) => state.user);
  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const { statusLabel, statusBadge, statusIcon } = getBoardStatusData(
    board,
    boardType
  );

  const {
    statusLabel: councilStatusLabel,
    statusBadge: councilStatusBadge,
    statusValue: councilStatusValue,
  } = getCouncilStatusData(board as Council);

  const {
    data: { visibilityLabel, visibilityBadge },
  } = useGetVisibility({ board });

  const handleClickCouncil = () => {
    const currentMemberId =
      memberId ||
      getMemberIdFromCouncilParticipants(
        user.email,
        board?.participants as CouncilParticipant[]
      );

    if (!isAdmin && !currentMemberId) return;

    const newRoute = getCouncilDetailsUrlByRole(
      board as Council,
      isAdmin,
      isDirector,
      currentMemberId,
      actualSociety
    );

    if (newRoute) {
      history.push(newRoute);
    }
  };

  const handleClickBasicBoard = () => {
    history.push(`/detalle-junta-basica/${societyId}/${board._id}`);
  };

  const handleRemoveBoard = () => {
    onRemoveBoard(index);
  };

  const handleClickRow = () => {
    switch (boardType) {
      case boardEntityTypes.COUNCIL:
        handleClickCouncil();
        break;
      case boardEntityTypes.BASIC_BOARD:
        handleClickBasicBoard();
        break;
      default:
        break;
    }
  };

  const toggleOptions = [
    <a
      className="d-flex cursor-pointer"
      key={`member-remove-option-${index}`}
      onClick={handleRemoveBoard}
    >
      <em className="icon ni ni-trash fs-20px" />
      <span className="ml-1">{t('Delete')}</span>
    </a>,
  ];

  return (
    <>
      <tr className="nk-tb-item cursor-pointer">
        <td className="nk-tb-col nk-tb-col-check d-none">
          <div className="custom-control custom-control-sm custom-checkbox notext">
            <input
              type="checkbox"
              className="custom-control-input"
              id="did-01"
            />
            <label className="custom-control-label" htmlFor="did-01" />
          </div>
        </td>

        <td className="nk-tb-col w-50px" onClick={handleClickRow}>
          <div className="caption-text sub-text fs-12px mt-1 bg-white w-20px h-20px rounded-circle text-center mx-auto lh-20px">
            {index + 1}
          </div>
        </td>

        <td className="nk-tb-col" onClick={handleClickRow}>
          <div className="caption-text fs-14px mt-1">{board.name}</div>
        </td>

        <td className="nk-tb-col tb-col-sm" onClick={handleClickRow}>
          {boardType === boardEntityTypes.COUNCIL ? (
            // @ts-ignore
            <CouncilBadges.Status
              t={t}
              style={councilStatusBadge}
              text={councilStatusLabel}
              status={councilStatusValue as keyof typeof councilStatus}
            />
          ) : (
            <BoardBadge
              badge={statusBadge}
              text={statusLabel}
              icon={statusIcon}
            />
          )}
        </td>

        <td className="nk-tb-col tb-col-md" onClick={handleClickRow}>
          <div className="caption-text fs-14px mt-1 text-soft d-inline-flex flex-wrap gx-2">
            <div>{format(new Date(board.date), DATE_FORMAT)}</div>
            <div>{format(new Date(board.date), TIME_FORMAT)}</div>
          </div>
        </td>

        {isAdmin && (
          <td className="nk-tb-col" onClick={handleClickRow}>
            <span className={`badge badge-dot badge-${visibilityBadge}`}>
              {t(visibilityLabel)}
            </span>
          </td>
        )}

        {isAdmin && (
          <td className="nk-tb-col" key={`board-options-${index}`}>
            <ToggleMenu toggleOptions={toggleOptions} />
          </td>
        )}
      </tr>
    </>
  );
};

export default RecentBoardsSectionRow;
